import { useQuery } from '@tanstack/react-query'
import { App as AntdApp, Button, Tooltip } from 'antd'
import { Link2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { organizationsOrganizationsRetrieveOptions } from 'src/api/main'
import { buildWorkspaceJoinUrl } from 'src/features/workspaces/utils/inviteUrl'

type Props = {
  workspaceId: number
}

/**
 * Copies the workspace's universal self-service join link. Enabled only when
 * `allow_self_service_join` is on for the workspace; otherwise the button is
 * disabled with a hint pointing at workspace settings.
 */
export const WorkspaceJoinLinkButton = ({ workspaceId }: Props) => {
  const { t } = useTranslation()
  const { message } = AntdApp.useApp()

  const { data: organization } = useQuery({
    ...organizationsOrganizationsRetrieveOptions({ path: { id: workspaceId } }),
    enabled: Number.isFinite(workspaceId) && workspaceId > 0
  })

  const enabled = Boolean(organization?.allow_self_service_join)
  const domain = organization?.domain_name

  const copy = async () => {
    if (!domain) return
    const url = buildWorkspaceJoinUrl(domain)
    try {
      await navigator.clipboard.writeText(url)
      message.success(t('users.lifecycle.joinLink.copied'))
    } catch {
      // Clipboard can be blocked (insecure context / permissions) — surface the
      // URL so the admin can copy it manually.
      message.info(url)
    }
  }

  const button = (
    <Button
      size="small"
      icon={<Link2 size={14} />}
      onClick={() => void copy()}
      disabled={!enabled || !domain}
    >
      {t('users.lifecycle.joinLink.action')}
    </Button>
  )

  if (enabled) return button

  return (
    <Tooltip title={t('users.lifecycle.joinLink.disabledHint')}>
      <span>{button}</span>
    </Tooltip>
  )
}
